import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orpc } from 'src/api/rpc/client';
import { Action, Subject } from '@erp/authz';
import { useListToolCatalog } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { useAgentDefinitionForm } from './AgentDefinitionFormContext';
import {
  CircleNotchIcon,
  ShieldCheckIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Badge } from 'src/components/ui/badge';
import {
  PermissionGrid,
  type GridAction,
  type GridGroup,
} from 'src/components/permissions/PermissionGrid';
import { PermissionListPopover } from 'src/components/permissions/PermissionListPopover';
import { formatPermissionSubjectLabel } from 'src/components/permissions/permissionDisplay';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog';
import { useToast } from 'src/context/ToastContext';
import { T, Var, useGT } from 'gt-react';

type PermissionRemovalTarget = {
  action: string;
  subject: string;
  toolTypes: string[];
  toolNames: string[];
};

// fallow-ignore-next-line complexity
export function AgentDefinitionPermissionsTab() {
  const gt = useGT();
  const { showToast } = useToast();

  const actionLabels = useMemo(
    (): Record<string, string> => ({
      create: gt('Create', { context: 'permission action' }),
      read: gt('View', { context: 'permission action' }),
      update: gt('Edit', { context: 'permission action' }),
      delete: gt('Delete', { context: 'permission action' }),
      approve: gt('Approve', { context: 'permission action' }),
      execute: gt('Execute', { context: 'permission action' }),
      report: gt('Report', { context: 'permission action' }),
      [Action.ChangePlanningLevelRules]: gt('Change Planning Level Rules', {
        context: 'permission action label',
      }),
    }),
    [gt],
  );

  const { definition, enabledTools, setLocalTools, isEditable } =
    useAgentDefinitionForm();
  const { data: catalogData, isLoading: catalogLoading } = useListToolCatalog();

  const { data: availableData, isLoading: availableLoading } = useQuery(
    orpc.authz.listAvailablePermissions.queryOptions(),
  );

  const [removalTarget, setRemovalTarget] =
    useState<PermissionRemovalTarget | null>(null);

  const enabledToolTypes = useMemo(() => enabledTools, [enabledTools]);
  const enabledToolTypeSet = useMemo(
    () => new Set(enabledToolTypes),
    [enabledToolTypes],
  );

  // "action:subject" -> { toolTypes[], toolNames[] }
  const permissionToolMap = useMemo(() => {
    const tools = catalogData?.tools ?? [];
    const map = new Map<string, { toolTypes: string[]; toolNames: string[] }>();
    for (const tool of tools) {
      if (!enabledToolTypeSet.has(tool.type)) continue;
      for (const perm of tool.requiredPermissions ?? []) {
        const key = `${perm.action}:${perm.subject}`;
        const existing = map.get(key) ?? { toolTypes: [], toolNames: [] };
        if (!existing.toolTypes.includes(tool.type)) {
          existing.toolTypes.push(tool.type);
          existing.toolNames.push(tool.name);
        }
        map.set(key, existing);
      }
    }
    return map;
  }, [catalogData?.tools, enabledToolTypeSet]);

  const allPermissions = useMemo(
    () =>
      Array.from(permissionToolMap.keys()).map((key) => {
        const [action, subject] = key.split(':');
        return { action, subject };
      }),
    [permissionToolMap],
  );

  const totalPermissions = permissionToolMap.size;

  const effectivePermissionsSummary = useMemo(() => {
    const p = totalPermissions;
    const t = enabledToolTypeSet.size;
    if (p === 1 && t === 1) {
      return gt('1 permission derived from 1 enabled tool', {
        context: 'agent tools effective permissions summary',
      });
    }
    if (p === 1 && t !== 1) {
      return gt('1 permission derived from {toolCount} enabled tools', {
        context: 'agent tools effective permissions summary',
        toolCount: String(t),
      });
    }
    if (p !== 1 && t === 1) {
      return gt('{permCount} permissions derived from 1 enabled tool', {
        context: 'agent tools effective permissions summary',
        permCount: String(p),
      });
    }
    return gt(
      '{permCount} permissions derived from {toolCount} enabled tools',
      {
        context: 'agent tools effective permissions summary',
        permCount: String(p),
        toolCount: String(t),
      },
    );
  }, [enabledToolTypeSet.size, gt, totalPermissions]);

  const handleRemovePermission = useCallback(
    (rowKey: string, actionKey: string) => {
      const key = `${actionKey}:${rowKey}`;
      const entry = permissionToolMap.get(key);
      if (!entry) return;
      setRemovalTarget({
        action: actionKey,
        subject: rowKey,
        toolTypes: entry.toolTypes,
        toolNames: entry.toolNames,
      });
    },
    [permissionToolMap],
  );

  const confirmRemoval = useCallback(() => {
    if (!removalTarget || !definition) return;
    const toolTypesToRemove = new Set(removalTarget.toolTypes);
    const remainingTools = enabledToolTypes.filter(
      (t) => !toolTypesToRemove.has(t),
    );

    setLocalTools(remainingTools);
    showToast({
      title: gt('Tools removed from draft', {
        context: 'toast title after removing tools from an agent draft',
      }),
      message: gt('Save the draft to keep this change.', {
        context: 'toast body after removing tools from an agent draft',
      }),
      color: 'success',
    });
    setRemovalTarget(null);
  }, [
    removalTarget,
    definition,
    enabledToolTypes,
    setLocalTools,
    showToast,
    gt,
  ]);

  // Close dialog if the permission disappears (e.g. after mutation)
  useEffect(() => {
    if (!removalTarget) return;
    const key = `${removalTarget.action}:${removalTarget.subject}`;
    if (!permissionToolMap.has(key)) {
      setRemovalTarget(null);
    }
  }, [permissionToolMap, removalTarget]);

  if (!definition || catalogLoading || availableLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!availableData) return null;

  const { actions, domainGroups, validActionsPerSubject } = availableData;
  const displayActions = actions.filter((a) => a !== Action.Manage);

  function isActionValidForSubject(action: Action, subject: Subject): boolean {
    const validActions = validActionsPerSubject[subject];
    return validActions?.includes(action) ?? false;
  }

  const gridActions: GridAction[] = displayActions.map((action) => ({
    key: action,
    label: actionLabels[action] ?? action,
  }));

  const allGridGroups: GridGroup[] = domainGroups
    .map((domain) => {
      const sections = domain.subdomains
        .map((subdomain) => {
          const rows = subdomain.subjects
            .map((subject) => {
              const cells = displayActions.map((action) => {
                const key = `${action}:${subject}`;
                const entry = permissionToolMap.get(key);
                const isValid = isActionValidForSubject(action, subject);

                return {
                  actionKey: action,
                  checked: !!entry && entry.toolNames.length > 0,
                  invalid: !isValid,
                  popoverLines: entry?.toolNames.length
                    ? entry.toolNames
                    : undefined,
                };
              });

              const hasActive = cells.some((c) => !c.invalid && c.checked);

              return {
                key: subject,
                label: formatPermissionSubjectLabel(subject),
                cells,
                hasActive,
              };
            })
            .filter((r) => r.hasActive);

          return {
            key: subdomain.subdomain,
            label: subdomain.subdomain,
            rows: rows.map(({ hasActive: _, ...r }) => r),
          };
        })
        .filter((s) => s.rows.length > 0);

      return {
        key: domain.domain,
        label: domain.domain,
        sections,
      };
    })
    .filter((g) => g.sections.length > 0);

  return (
    <div className="space-y-2">
      {/* Summary header */}
      <div className="flex items-center gap-3 rounded-lg border p-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
          <ShieldCheckIcon className="h-4 w-4 text-primary" />
        </div>
        <div>
          <p className="text-sm font-medium">
            <T>Effective Permissions</T>
          </p>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {totalPermissions > 0 ? (
              <PermissionListPopover permissions={allPermissions}>
                <Badge
                  variant="secondary"
                  className="text-xs h-5 cursor-pointer"
                >
                  {totalPermissions}
                </Badge>
              </PermissionListPopover>
            ) : (
              <Badge variant="secondary" className="text-xs h-5">
                {totalPermissions}
              </Badge>
            )}
            <span>{effectivePermissionsSummary}</span>
          </div>
        </div>
      </div>

      {/* Permission grid — onToggle triggers removal confirmation */}
      {allGridGroups.length > 0 ? (
        <PermissionGrid
          actions={gridActions}
          groups={allGridGroups}
          onToggle={isEditable ? handleRemovePermission : undefined}
          checkedOnlyToggle
        />
      ) : (
        <p className="text-sm text-muted-foreground text-center py-8">
          <T>
            No tools are enabled for this agent, or the enabled tools have no
            declared permissions. Assign tools on the Tools tab.
          </T>
        </p>
      )}

      {/* Removal confirmation dialog */}
      <AlertDialog
        open={!!removalTarget}
        onOpenChange={(open) => {
          if (!open) setRemovalTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="confirm dialog title for removing a permission">
                Remove permission?
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  <T context="explains that removing a permission removes the tools that grant it">
                    The{' '}
                    <Var>
                      <span className="font-medium text-foreground">
                        {removalTarget?.action}{' '}
                        {formatPermissionSubjectLabel(
                          removalTarget?.subject ?? '',
                        )}
                      </span>
                    </Var>{' '}
                    permission is granted by the following tools. Removing this
                    permission will remove these tools from the agent:
                  </T>
                </p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  {removalTarget?.toolNames.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <T context="cancel button in remove permission dialog">Cancel</T>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoval}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <TrashIcon className="h-4 w-4 mr-1.5" />
              <T context="confirm button to remove tools that grant a permission">
                Remove tools
              </T>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
